import { etDateKey, sessionCloseEpochMs, sessionOpenEpochMs } from './marketHours';

const MINUTE_MS = 60_000;

export function replayMinuteKey(t) {
  return Math.floor(Number(t) / MINUTE_MS);
}

function annotateGapSpans(markers) {
  let index = 0;
  while (index < markers.length) {
    if (markers[index].kind !== 'gap') {
      index += 1;
      continue;
    }
    let end = index;
    while (end + 1 < markers.length && markers[end + 1].kind === 'gap') end += 1;
    const startT = markers[index].t;
    const endT = markers[end].t;
    for (let cursor = index; cursor <= end; cursor += 1) {
      markers[cursor] = {
        ...markers[cursor],
        gapStartT: startT,
        gapEndT: endT,
      };
    }
    index = end + 1;
  }
  return markers;
}

// One marker per ET minute across the requested bounds and stored frames.
// Missing minutes are `kind: 'gap'` with no frameIndex -- never a synthesized
// frame. Collapsing to present frames only is what made a 50-minute hole look
// like adjacent time on the scrubber.
export function buildMinuteMarkers(frames = [], { startT = null, endT = null } = {}) {
  const byKey = new Map();
  (frames || []).forEach((frame, frameIndex) => {
    if (!Number.isFinite(frame?.t)) return;
    const key = replayMinuteKey(frame.t);
    byKey.set(key, {
      t: frame.t,
      frameIndex,
      kind: 'frame',
      // Recovered minutes carry chain rows only. They are real frames, so they
      // are not gaps -- but the scrubber has to say so, or a resync that fills a
      // hole with chain-only frames reads as full hydration.
      partial: frame.partial === true,
    });
  });
  if (!byKey.size) return [];

  const keys = [...byKey.keys()].sort((left, right) => left - right);
  const firstKey = Number.isFinite(startT)
    ? Math.min(replayMinuteKey(startT), keys[0])
    : keys[0];
  const lastKey = Number.isFinite(endT)
    ? Math.max(replayMinuteKey(endT), keys[keys.length - 1])
    : keys[keys.length - 1];
  const markers = [];
  for (let key = firstKey; key <= lastKey; key += 1) {
    const frame = byKey.get(key);
    markers.push(frame || {
      t: key * MINUTE_MS,
      frameIndex: null,
      kind: 'gap',
    });
  }
  return annotateGapSpans(markers);
}

function bandsMatching(markers, matches) {
  const bands = [];
  if (!markers.length) return bands;
  let index = 0;
  while (index < markers.length) {
    if (!matches(markers[index])) {
      index += 1;
      continue;
    }
    let end = index;
    while (end + 1 < markers.length && matches(markers[end + 1])) end += 1;
    bands.push({
      startIndex: index,
      endIndex: end,
      startT: markers[index].t,
      endT: markers[end].t,
    });
    index = end + 1;
  }
  return bands;
}

export function replayGapBands(markers = []) {
  return bandsMatching(markers, (marker) => marker.kind === 'gap');
}

// Spans recovered from chain snapshots: real minutes, but chain data only, so
// index-fed surfaces cannot advance through them. Rendered distinctly from gaps
// because the user can still scrub them -- they are just thinner than they look.
export function replayPartialBands(markers = []) {
  return bandsMatching(markers, (marker) => marker.kind === 'frame' && marker.partial === true);
}

export function countReplayPartialMinutes(frames = []) {
  return buildMinuteMarkers(frames).filter(
    (marker) => marker.kind === 'frame' && marker.partial === true
  ).length;
}

export function replayGapBandStyle(band, markerCount) {
  const span = Math.max(1, (markerCount || 1) - 1);
  return {
    left: `${(band.startIndex / span) * 100}%`,
    width: `${((band.endIndex - band.startIndex + 1) / span) * 100}%`,
  };
}

export function countReplayGaps(frames = []) {
  return buildMinuteMarkers(frames).filter((marker) => marker.kind === 'gap').length;
}

export function replayExpectedEndT(frames = [], now = Date.now()) {
  const valid = (frames || [])
    .filter((frame) => Number.isFinite(frame?.t))
    .sort((left, right) => left.t - right.t);
  if (!valid.length) return null;
  const last = valid[valid.length - 1];
  const sessionLastMinute = replayMinuteKey(sessionCloseEpochMs(last.t)) - 1;
  const expectedLastMinute = etDateKey(last.t) === etDateKey(now)
    ? Math.min(replayMinuteKey(now), sessionLastMinute)
    : sessionLastMinute;
  return expectedLastMinute * MINUTE_MS;
}

export function countReplayCoverageGaps(frames = [], now = Date.now()) {
  const valid = (frames || [])
    .filter((frame) => Number.isFinite(frame?.t))
    .sort((left, right) => left.t - right.t);
  if (!valid.length) return null;
  return buildMinuteMarkers(valid, {
    startT: sessionOpenEpochMs(valid[0].t),
    endT: replayExpectedEndT(valid, now),
  }).filter((marker) => marker.kind === 'gap').length;
}

export function replayCoverageNeedsRecovery(frames = [], now = Date.now()) {
  return countReplayCoverageGaps(frames, now) !== 0;
}

export function lastFrameIndexBefore(frames, t) {
  let result = -1;
  (frames || []).forEach((frame, index) => {
    if (Number.isFinite(frame?.t) && frame.t < t) result = index;
  });
  return result;
}

// Local captured frames (no partial marker) always win. A recovered minute
// never replaces a richer local one; resyncing twice is a no-op on already
// recovered minutes.
export function mergeRecoveredReplayFrames(localFrames = [], recoveredFrames = []) {
  const byMinute = new Map();
  for (const frame of localFrames || []) {
    if (!Number.isFinite(frame?.t)) continue;
    byMinute.set(replayMinuteKey(frame.t), frame);
  }
  for (const frame of recoveredFrames || []) {
    if (!Number.isFinite(frame?.t) || !frame?.options?.rowsBySym) continue;
    const key = replayMinuteKey(frame.t);
    const existing = byMinute.get(key);
    if (!existing) {
      byMinute.set(key, frame);
      continue;
    }
    if (
      (existing.datasetAnchor === true && frame.datasetAnchor !== true)
      || (existing.partial === true && frame.partial !== true)
    ) {
      byMinute.set(key, frame);
    }
  }
  return [...byMinute.values()].sort((left, right) => left.t - right.t);
}

export function recoveredApiFrameToBufferFrame(apiFrame) {
  if (!apiFrame || !Number.isFinite(apiFrame.t) || !apiFrame.rowsBySym) return null;
  if (!Number.isFinite(apiFrame.spot)) return null;
  if (!Object.keys(apiFrame.rowsBySym).length) return null;
  return {
    t: apiFrame.t,
    options: { rowsBySym: apiFrame.rowsBySym, spot: apiFrame.spot },
    greeks: { dynamicLevels: {}, topOptions: {} },
    aggregationState: {},
    partial: true,
  };
}
